import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, formatError, wasteLabel, wasteColor } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import StatusBadge from "@/components/StatusBadge";
import VerifyBarcodeDialog from "@/components/VerifyBarcodeDialog";
import { Users, Clock, Check, Ban, Award, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function OperatorQueue() {
  const [subs, setSubs] = useState([]);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("pending");
  const [loading, setLoading] = useState(true);

  const reload = async (status) => {
    setLoading(true);
    try {
      const [{ data }, s] = await Promise.all([api.get(`/submissions?status=${status}`), api.get("/stats/operator")]);
      setSubs(data);
      setStats(s.data);
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { reload(tab); }, [tab]);

  return (
    <Layout title="Coda conferimenti" subtitle="Rivedi, certifica o contesta i conferimenti caricati dai cittadini e dalle aziende." actions={<VerifyBarcodeDialog />}>
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <MetricCard icon={Users} label="Utenti attivi" value={stats?.users} tone="primary" testid="mtr-users" />
        <MetricCard icon={Clock} label="In attesa" value={stats?.pending} tone="warning" testid="mtr-pending" />
        <MetricCard icon={Check} label="Approvati" value={stats?.approved} tone="success" testid="mtr-approved" />
        <MetricCard icon={Ban} label="Contestati" value={stats?.rejected} tone="danger" testid="mtr-rejected" />
        <MetricCard icon={Award} label="TARI" value={stats?.recognized} tone="info" testid="mtr-recognized" />
      </div>

      <div className="flex items-center justify-between mb-4">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="queue-tabs">
            <TabsTrigger value="pending" data-testid="tab-pending">In attesa</TabsTrigger>
            <TabsTrigger value="approved" data-testid="tab-approved">Approvati</TabsTrigger>
            <TabsTrigger value="rejected" data-testid="tab-rejected">Contestati</TabsTrigger>
            <TabsTrigger value="recognized" data-testid="tab-recognized">TARI</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-sm text-muted-foreground">Caricamento…</div>
          ) : subs.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">Nessun conferimento in questo stato.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Utente</TableHead>
                  <TableHead>Codice</TableHead>
                  <TableHead>Tipologia</TableHead>
                  <TableHead>Peso</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subs.map((s) => (
                  <TableRow key={s.id} data-testid={`op-row-${s.id}`} className="hover:bg-gray-50">
                    <TableCell className="text-xs text-muted-foreground">{new Date(s.created_at).toLocaleString("it-IT")}</TableCell>
                    <TableCell>
                      <div className="font-medium text-sm">{s.user_name}</div>
                      <div className="text-xs text-muted-foreground">{s.user_email}</div>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{s.barcode_full || <span className="text-amber-600 font-sans">da assegnare</span>}</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ background: wasteColor(s.waste_type) }} />
                        {wasteLabel(s.waste_type)}
                      </span>
                    </TableCell>
                    <TableCell className="font-medium">
                      {s.verified_weight_kg != null ? `${s.verified_weight_kg} kg`
                        : s.declared_weight_kg != null ? <span className="text-muted-foreground">{s.declared_weight_kg} kg (dich.)</span>
                        : "—"}
                    </TableCell>
                    <TableCell><StatusBadge status={s.status} /></TableCell>
                    <TableCell className="text-right pr-6 space-x-2">
                      <Link to={`/operator/review/${s.id}`} className="text-primary text-sm font-medium hover:underline" data-testid={`review-${s.id}`}>
                        Revisiona →
                      </Link>
                      <Link to={`/operator/user/${s.user_id}`} className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-0.5" data-testid={`user-${s.user_id}`}>
                        <ExternalLink className="w-3 h-3" /> utente
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Layout>
  );
}

function MetricCard({ icon: Icon, label, value, tone, testid }) {
  const tones = {
    primary: "bg-primary/10 text-primary",
    success: "bg-emerald-100 text-emerald-700",
    warning: "bg-amber-100 text-amber-700",
    danger: "bg-red-100 text-red-700",
    info: "bg-blue-100 text-blue-700",
  };
  return (
    <Card data-testid={testid}>
      <CardContent className="p-5 flex items-center gap-4">
        <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${tones[tone]}`}><Icon className="w-5 h-5" /></div>
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
          <div className="font-display text-2xl font-semibold">{value ?? "—"}</div>
        </div>
      </CardContent>
    </Card>
  );
}
